<script>
import BorderedInput from "./../Redesign/BorderedInput";
import treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import AddElementCard from "../AddElementCard.vue";

export default {
  name: "WorktimeEditor",

  components: {
    AddElementCard,
    BorderedInput,
    treeselect,
  },

  props: {
    currentDate: {
      required: false,
      type: String,
      default: new Date().toISOString().split("T")[0],
    },
    worktime: {
      required: false,
      type: Array,
    },
    userWorkFunctions: {
      required: true,
      type: Array,
    },
    costCarriers: {
      required: true,
      type: Array,
    },
    validationErrors: {
      required: false,
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      required: false,
      type: Boolean,
      default: false,
    },
    userId: {
      required: false,
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      date: new Date().toISOString().split("T")[0],
      rows: [],
      errors: {},
      serverErrors: null,
      dirtyFields: new Set(),
      dayOccupied: null,
      showChangeModal: false,
      changeComment: '',
      changeCommentError: false,
      previousDate: null,
      pendingDate: null,
      afterSaveDate: '',
      baselineRows: '[]',
      showUnsavedModal: false,
      isEditState: false,
    }
  },

  mounted() {
    this.date = this.currentDate;
    this.previousDate = this.currentDate;
    this.isEditState = this.isEdit;

    if (this.worktime) {
      this.loadRows(this.worktime);
    }

    this.debouncedValidate = _.debounce(this.validate, 500);
    if (Object.keys(this.validationErrors).length > 0) {
      this.dirtyFields.add('*');
    }
    this.parseValidationErrors();
    this.captureBaseline();
    // Occupancy notice only; rows are already seeded from the server prop above.
    this.fetchDayInfo(false);
  },

  computed: {
    totalWorktime() {
      let totalMinutes = 0;

      this.rows.forEach(row => {
        totalMinutes += this.entryWorktimeMinutes(row);
      });

      return totalMinutes;
    },
    totalWorktimeFormatted() {
      return this.formatMinutes(this.totalWorktime);
    },
    // Options für den Treeselect: nicht-bebuchbare Parents (isDisabled) werden
    // ausgeblendet, ihre bebuchbaren Children rücken an deren Stelle nach oben.
    visibleCostCarriers() {
      return this.filterBookableCarriers(this.costCarriers);
    },
    totalBreakMinutes() {
      let totalMinutes = 0;

      this.rows.forEach((row, index) => {
        totalMinutes += this.entryBreakMinutes(row);
        totalMinutes += this.breakMinutesSinceLastEntry(index);
      });

      return totalMinutes;
    },
    hasFaultyBreakTimes() {
      let worktime = this.totalWorktime;
      let breakTime = this.totalBreakMinutes;

      if (worktime > 60 * 6 && breakTime < 30) {
        return true;
      } else if (worktime > 60 * 9 && breakTime < 45) {
        return true;
      }

      return false;
    }
  },

  methods: {
    timeToMinutes(time) {
      if (!time) {
        return null;
      }

      const parts = time.split(':');
      if (parts.length !== 2) {
        return null;
      }

      return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    },
    getMinutes(start, end) {
      const startMin = this.timeToMinutes(start);
      const endMin = this.timeToMinutes(end);

      if (startMin === null || endMin === null) {
        return 0;
      }

      let adjustedEnd = endMin < startMin ? endMin + 24 * 60 : endMin;

      return Math.max(0, adjustedEnd - startMin);
    },
    normalizeTimeRange(start, end) {
      const startMin = this.timeToMinutes(start);
      const endMin = this.timeToMinutes(end);

      if (startMin === null || endMin === null) {
        return null;
      }

      return {
        start: startMin,
        end: endMin < startMin ? endMin + 24 * 60 : endMin
      };
    },
    mergeIntervals(intervals) {
      if (intervals.length === 0) {
        return [];
      }

      intervals.sort((a, b) => a.start - b.start);

      const merged = [intervals[0]];

      for (let i = 1; i < intervals.length; i++) {
        const current = intervals[i];
        const last = merged[merged.length - 1];

        if (current.start <= last.end) {
          last.end = Math.max(last.end, current.end);
        } else {
          merged.push(current);
        }
      }

      return merged;
    },
    getTotalMinutesFromIntervals(intervals) {
      return intervals.reduce((total, interval) => total + (interval.end - interval.start), 0);
    },
    collectBreakIntervals(breaks) {
      const intervals = [];

      breaks.forEach(break_ => {
        const range = this.normalizeTimeRange(break_.start, break_.end);

        if (!range) {
          return;
        }

        intervals.push(range);
      });

      return intervals;
    },
    entryBreakMinutes(row) {
      if (!row.breaks || row.breaks.length === 0) {
        return 0;
      }

      const intervals = this.collectBreakIntervals(row.breaks);
      const merged = this.mergeIntervals(intervals);
      return this.getTotalMinutesFromIntervals(merged);
    },
    entryWorktimeMinutes(row) {
      let duration = this.getMinutes(row.start, row.end);
      return Math.max(0, duration - this.entryBreakMinutes(row));
    },
    entryBreakFormatted(row) {
      return this.formatMinutes(this.entryBreakMinutes(row));
    },
    entryWorktimeFormatted(row) {
      return this.formatMinutes(this.entryWorktimeMinutes(row));
    },
    breakMinutesSinceLastEntry(row) {
      if (row > 0) {
        const current = this.rows[row];
        const last = this.rows[row - 1];

        if (current.start && last.end) {
          return this.getMinutes(last.end, current.start);
        }
      }

      return 0;
    },
    breakMinutesSinceLastEntryFormatted(row) {
      const totalMinutes = this.breakMinutesSinceLastEntry(row);

      if (totalMinutes < 60) {
        return totalMinutes + 'min';
      } else {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        let result = hours + 'h';

        if (minutes > 0) {
          result += ' ' + minutes + 'min';
        }

        return result;
      }
    },
    formatMinutes(totalMinutes) {
      let hours = Math.floor(totalMinutes / 60);
      let minutes = totalMinutes % 60;

      return String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0');
    },
    addRow() {
      const lastRow = this.rows.length > 0 ? this.rows[this.rows.length - 1] : null;
      let startTime = null;

      if (lastRow) {
        const workEnd = this.normalizeTimeRange(lastRow.start, lastRow.end);

        if (workEnd && lastRow.breaks && lastRow.breaks.length > 0) {
          let latestTime = workEnd.end;

          lastRow.breaks.forEach(break_ => {
            const breakRange = this.normalizeTimeRange(break_.start, break_.end);
            if (breakRange && breakRange.end > latestTime) {
              latestTime = breakRange.end;
            }
          });

          const hours = Math.floor(latestTime / 60);
          const minutes = latestTime % 60;
          startTime = String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0');
        } else if (lastRow.end) {
          startTime = lastRow.end;
        }
      }

      let newVal = {
        'available_user_work_functions': [],
        'cost_carrier': null,
        'function': null,
        'description': '',
        'start': startTime,
        'end': null,
        'breaks': [
          {
            'start': null,
            'end': null,
          }
        ],
      };
      this.rows.push(newVal);
    },
    removeRow(index) {
      this.rows.splice(index, 1);
    },
    getCostCarrierLabel(id) {
      if (!id) return '';
      const found = this.findCostCarrierRecursive(this.costCarriers, id);
      return found ? found.label : '';
    },
    // Prüft, ob der Kostenträger für das oben gewählte Datum gültig ist.
    // valid_from/valid_to sind tagesgenaue 'YYYY-MM-DD'-Strings (oder null = offen).
    isCarrierValidForDate(carrier) {
      if (!this.date) {
        return true;
      }
      if (carrier.valid_from && this.date < carrier.valid_from) {
        return false;
      }
      if (carrier.valid_to && this.date > carrier.valid_to) {
        return false;
      }
      return true;
    },
    filterBookableCarriers(carriers) {
      const result = [];

      for (const carrier of carriers) {
        const children = carrier.children && carrier.children.length
            ? this.filterBookableCarriers(carrier.children)
            : [];

        // Ausblenden, wenn der User hier nicht buchen darf (isDisabled) ODER der
        // Kostenträger für das gewählte Datum nicht gültig ist. Bebuchbare/gültige
        // Children rücken in beiden Fällen an die Stelle des Parents nach oben.
        if (carrier.isDisabled || !this.isCarrierValidForDate(carrier)) {
          result.push(...children);
        } else {
          const node = {...carrier};
          if (children.length) {
            node.children = children;
          } else {
            delete node.children;
          }
          result.push(node);
        }
      }

      return result;
    },
    findCostCarrierRecursive(carriers, id) {
      for (const carrier of carriers) {
        if (parseInt(carrier.id) === parseInt(id)) {
          return carrier;
        }
        if (carrier.children && carrier.children.length > 0) {
          const found = this.findCostCarrierRecursive(carrier.children, id);
          if (found) return found;
        }
      }
      return null;
    },
    setCostCarrierInRow(index, costCarrier) {
      this.$set(this.rows[index], 'cost_carrier', costCarrier);
      this.$set(this.rows[index], 'function', null);

      if (costCarrier) {
        const costCarrierObj = this.findCostCarrierRecursive(this.costCarriers, costCarrier);
        const available = Object.values(costCarrierObj ? costCarrierObj.user_work_functions ?? [] : []);
        this.$set(this.rows[index], 'available_user_work_functions', this.userWorkFunctions.filter((f) => available.includes(parseInt(f.id))));
      } else {
        this.$set(this.rows[index], 'available_user_work_functions', []);
      }
    },
    addBreak(index) {
      const row = this.rows[index];

      const breaks = Array.isArray(row.breaks) ? row.breaks : [];

      const updatedRow = {
        ...row,
        breaks: [
          ...breaks,
          { start: null, end: null }
        ],
      };

      // wichtig: neues Array für rows
      this.rows.splice(index, 1, updatedRow);
    },
    removeBreak(entryIndex, breakIndex) {
      if (!this.rows[entryIndex].breaks) {
        this.rows[entryIndex].breaks = [];
      }
      this.rows[entryIndex].breaks.splice(breakIndex, 1);
    },
    parseValidationErrors() {
      const parsed = {};
      const source = this.serverErrors || this.validationErrors || {};

      Object.keys(source).forEach(key => {
        const messages = source[key];

        // Parse keys like "worktime.0.start" or "worktime.1.breaks.0.end"
        if (key.match(/^worktime\.(\d+)\.breaks\.(\d+)\.(\w+)$/)) {
          const matches = key.match(/^worktime\.(\d+)\.breaks\.(\d+)\.(\w+)$/);
          const rowIndex = parseInt(matches[1]);
          const breakIndex = parseInt(matches[2]);
          const field = matches[3];

          if (!parsed[rowIndex]) {
            parsed[rowIndex] = {};
          }
          if (!parsed[rowIndex].breaks) {
            parsed[rowIndex].breaks = {};
          }
          if (!parsed[rowIndex].breaks[breakIndex]) {
            parsed[rowIndex].breaks[breakIndex] = {};
          }

          parsed[rowIndex].breaks[breakIndex][field] = messages[0];
        } else if (key.match(/^worktime\.(\d+)\.(\w+)$/)) {
          const matches = key.match(/^worktime\.(\d+)\.(\w+)$/);
          const rowIndex = parseInt(matches[1]);
          const field = matches[2];

          if (!parsed[rowIndex]) {
            parsed[rowIndex] = {};
          }

          parsed[rowIndex][field] = messages[0];
        }
      });

      this.errors = parsed;
    },
    hasInput(row) {
      return row.cost_carrier || row.function || row.description || row.start || row.end;
    },
    validate() {
      if (!this.rows.some(row => this.hasInput(row))) {
        this.errors = {};
        return;
      }

      const payload = {
        worktime_date: this.date,
        worktime: this.rows.map(row => ({
          cost_carrier: row.cost_carrier,
          function: row.function,
          description: row.description,
          start: row.start,
          end: row.end,
          breaks: (row.breaks || []).filter(b => b.start || b.end).map(b => ({
            start: b.start,
            end: b.end,
          })),
        })),
      };

      window.axios.post('/track/validate', payload)
        .then(() => {
          this.errors = {};
        })
        .catch(error => {
          if (error.response && error.response.status === 422) {
            this.serverErrors = error.response.data.errors;
            this.parseValidationErrors();
          }
        });
    },
    dirtyKey(rowIndex, field, breakIndex = null) {
      return breakIndex !== null
        ? `${rowIndex}.breaks.${breakIndex}.${field}`
        : `${rowIndex}.${field}`;
    },
    markDirty(rowIndex, field, breakIndex = null) {
      this.dirtyFields.add(this.dirtyKey(rowIndex, field, breakIndex));
    },
    markAllDirty() {
      this.dirtyFields.add('*');
    },
    handleSubmit() {
      this.markAllDirty();
      if (this.hasFaultyBreakTimes || this.rows.length <= 0) {
        return;
      }
      if (this.isEditState) {
        this.changeComment = '';
        this.changeCommentError = false;
        this.showChangeModal = true;
      } else {
        document.getElementById('track-form').submit();
      }
    },
    confirmChange() {
      if (!this.changeComment.trim()) {
        this.changeCommentError = true;
        return;
      }
      this.showChangeModal = false;
      document.getElementById('track-form').submit();
    },
    // Maps the server worktime shape into reactive rows and restores each row's
    // available functions (setCostCarrierInRow resets `function`, so cache/restore it).
    loadRows(worktime) {
      this.rows = (worktime || []).map(w => ({
        ...w,
        available_user_work_functions: [],
        breaks: w.breaks || [],
      }));

      this.rows.forEach((value, index) => {
        const cachedFunction = value.function;
        this.setCostCarrierInRow(index, value.cost_carrier);
        value.function = cachedFunction;
      });
    },
    captureBaseline() {
      this.baselineRows = JSON.stringify(this.rows);
    },
    isDirty() {
      return JSON.stringify(this.rows) !== this.baselineRows;
    },
    fetchDayInfo(replaceRows = false) {
      if (!this.date) {
        this.dayOccupied = null;
        return;
      }
      window.axios.get('/track/day-info', {params: {date: this.date, user_id: this.userId}})
          .then(response => {
            this.dayOccupied = response.data.occupied ? response.data : null;

            if (replaceRows) {
              const worktime = response.data.worktime || [];
              this.loadRows(worktime);
              this.isEditState = worktime.length > 0;
              this.dirtyFields = new Set();
              this.serverErrors = null;
              this.errors = {};
              this.captureBaseline();
            }
          })
          .catch(() => {
            this.dayOccupied = null;
          });
    },
    onDateChange(event) {
      const newDate = event && event.target ? event.target.value : this.date;

      if (this.isDirty()) {
        // Revert the visible date and ask the user what to do with the unsaved input.
        this.date = this.previousDate;
        this.pendingDate = newDate;
        this.showUnsavedModal = true;
        return;
      }

      this.previousDate = newDate;
      this.debouncedValidate();
      this.fetchDayInfo(true);
    },
    discardAndSwitch() {
      this.showUnsavedModal = false;
      this.date = this.pendingDate;
      this.previousDate = this.pendingDate;
      this.pendingDate = null;
      this.fetchDayInfo(true);
    },
    saveAndSwitch() {
      // Persist the current day, then reopen the tracker on the selected date (server redirect).
      this.afterSaveDate = this.pendingDate;
      this.showUnsavedModal = false;
      this.handleSubmit();
    },
    getFieldError(rowIndex, field, breakIndex = null) {
      if (!this.dirtyFields.has('*') && !this.dirtyFields.has(this.dirtyKey(rowIndex, field, breakIndex))) {
        return null;
      }

      if (!this.errors[rowIndex]) {
        return null;
      }

      if (breakIndex !== null) {
        return this.errors[rowIndex].breaks?.[breakIndex]?.[field] || null;
      }

      return this.errors[rowIndex][field] || null;
    },
  }
}
</script>

<template>
  <div>
    <div class="tw-flex tw-items-center tw-justify-between">
      <div>
        <bordered-input>
          <template #label>
            <label for="worktime_date">
              Datum
            </label>
          </template>
          <template #input>
            <input class="tw-mr-4" id="worktime_date" name="worktime_date" type="date"
                   v-model="date" placeholder="Datum" @change="onDateChange($event)">
          </template>
        </bordered-input>
      </div>

      <div class="tw-w-32">
        <bordered-input>
          <template #label>
            <label for="worktime_total">
              Gesamt
            </label>
          </template>
          <template #input>
            <input class="tw-mr-4" id="worktime_total" name="worktime_total" type="time"
                   placeholder="Gesamt" v-model="totalWorktimeFormatted" disabled>
          </template>
        </bordered-input>
      </div>
    </div>

    <hr class="tw-my-4 tw-h-1 tw-bg-black"/>

    <div v-if="dayOccupied"
         class="tw-my-4 tw-p-3 tw-bg-yellow-100 tw-border tw-border-yellow-400 tw-rounded tw-text-yellow-800">
      <span class="tw-font-bold">Hinweis:</span>
      <template v-if="dayOccupied.type === 'holiday'">
        Dieser Tag ist ein
        <template v-if="dayOccupied.half_day">halber</template>
        gesetzlicher Feiertag (<strong>{{ dayOccupied.label }}</strong>).
      </template>
      <template v-else>
        Für diesen Tag liegt
        <template v-if="dayOccupied.half_day">eine halbtägige</template>
        <template v-else>eine</template>
        Abwesenheit vor (<strong>{{ dayOccupied.label }}</strong>).
      </template>
    </div>

    <div class="tw-mt-4" v-for="(value, index) in rows" v-if="rows.length > 0" :key="index">
      <div class="tw-font-bold tw-text-center" v-if="breakMinutesSinceLastEntry(index) > 0">
        {{ breakMinutesSinceLastEntryFormatted(index) }} Pause
      </div>

      <hr class="tw-my-4" v-if="breakMinutesSinceLastEntry(index) > 0"/>

      <div class="lg:tw-grid lg:tw-grid-cols-3 lg:tw-gap-4">
        <div>
          <bordered-input>
            <template #label>
              <label :for="'worktime_cost_carrier_' + index">
                Projekt
              </label>
            </template>
            <template #input>
              <div :title="getCostCarrierLabel(value.cost_carrier)">
                <treeselect
                    :class="{'tw-border-red-500': getFieldError(index, 'cost_carrier')}"
                    :multiple="false"
                    :flat="true"
                    :name="'worktime[' + index + '][cost_carrier]'"
                    :options="visibleCostCarriers"
                    :value="value.cost_carrier"
                    noResultsText="Nichts gefunden"
                    placeholder="Projekt"
                    @input="markDirty(index, 'cost_carrier'); setCostCarrierInRow(index, $event); debouncedValidate()">
                </treeselect>
              </div>
            </template>
          </bordered-input>
          <p v-if="getFieldError(index, 'cost_carrier')" class="tw-text-red-600 tw-text-sm tw-mt-1">
            {{ getFieldError(index, 'cost_carrier') }}
          </p>
        </div>

        <div class="tw-mt-2 lg:tw-mt-0">
          <bordered-input>
            <template #label>
              <label :for="'worktime_function_' + index">
                Gewerk
              </label>
            </template>
            <template #input>
              <treeselect
                  :multiple="false"
                  v-model="value.function"
                  :name="'worktime[' + index + '][function]'"
                  :options="value.available_user_work_functions"
                  noResultsText="Nichts gefunden"
                  placeholder="Gewerk"
                  :class="{'tw-border-red-500': getFieldError(index, 'function')}"
                  @input="markDirty(index, 'function'); debouncedValidate()"
              >
              </treeselect>
            </template>
          </bordered-input>
          <p v-if="getFieldError(index, 'function')" class="tw-text-red-600 tw-text-sm tw-mt-1">
            {{ getFieldError(index, 'function') }}
          </p>
        </div>

        <div class="tw-mt-2 lg:tw-mt-0">
          <bordered-input>
            <template #label>
              <label :for="'worktime_description_' + index">
                Beschreibung
              </label>
            </template>
            <template #input>
              <input class="tw-mr-4" :id="'worktime_description_' + index"
                     :name="'worktime[' + index + '][description]'"
                     placeholder="Beschreibung" v-model="value.description"
                     :class="{'tw-border-red-500': getFieldError(index, 'description')}"
                     @blur="markDirty(index, 'description')"
                     @input="debouncedValidate()">
            </template>
          </bordered-input>
          <p v-if="getFieldError(index, 'description')" class="tw-text-red-600 tw-text-sm tw-mt-1">
            {{ getFieldError(index, 'description') }}
          </p>
        </div>
      </div>

      <div class="lg:tw-grid lg:tw-grid-cols-3 lg:tw-gap-12 lg:items-stretch tw-mt-4">
        <div>
          <p class="tw-font-bold tw-mb-2">Arbeitszeit</p>
          <div class="tw-grid tw-grid-cols-2 tw-gap-4">
            <div>
              <bordered-input>
                <template #label>
                  <label :for="'worktime_start_' + index">
                    von
                  </label>
                </template>
                <template #input>
                  <input class="tw-mr-4" :id="'worktime_start_' + index" :name="'worktime[' + index + '][start]'"
                         type="time"
                         placeholder="Start" v-model="value.start"
                         :class="{'tw-border-red-500': getFieldError(index, 'start')}"
                         @blur="markDirty(index, 'start')"
                         @change="debouncedValidate()">
                </template>
              </bordered-input>
              <p v-if="getFieldError(index, 'start')" class="tw-text-red-600 tw-text-sm tw-mt-1">
                {{ getFieldError(index, 'start') }}
              </p>
            </div>
            <div>
              <bordered-input>
                <template #label>
                  <label :for="'worktime_end_' + index">
                    bis
                  </label>
                </template>
                <template #input>
                  <input class="tw-mr-4" :id="'worktime_end_' + index" :name="'worktime[' + index + '][end]'"
                         type="time"
                         placeholder="Ende" v-model="value.end"
                         :class="{'tw-border-red-500': getFieldError(index, 'end')}"
                         @blur="markDirty(index, 'end')"
                         @change="debouncedValidate()">
                </template>
              </bordered-input>
              <p v-if="getFieldError(index, 'end')" class="tw-text-red-600 tw-text-sm tw-mt-1">
                {{ getFieldError(index, 'end') }}
              </p>
            </div>
          </div>
        </div>

        <div>
          <p class="tw-font-bold tw-mb-2">Pause</p>
          <div class="tw-grid tw-grid-cols-5 tw-gap-4 tw-mt-2 tw-items-start" v-for="(breakEntry, breakIndex) in value.breaks"
               v-if="value.breaks.length > 0" :key="breakIndex">
            <div class="tw-col-span-2">
              <bordered-input>
                <template #label>
                  <label :for="'worktime_break_start_' + index + '_' + breakIndex">
                    von
                  </label>
                </template>
                <template #input>
                  <input class="tw-mr-4" :id="'worktime_break_start_' + index + '_' + breakIndex"
                         :name="'worktime[' + index + '][breaks][' + breakIndex + '][start]'"
                         type="time" :disabled="!value.start || !value.end"
                         placeholder="Start" v-model="breakEntry.start"
                         :class="{'tw-border-red-500': getFieldError(index, 'start', breakIndex)}"
                         @blur="markDirty(index, 'start', breakIndex)"
                         @change="debouncedValidate()">
                </template>
              </bordered-input>
              <p v-if="getFieldError(index, 'start', breakIndex)" class="tw-text-red-600 tw-text-sm tw-mt-1">
                {{ getFieldError(index, 'start', breakIndex) }}
              </p>
            </div>
            <div class="tw-col-span-2">
              <bordered-input>
                <template #label>
                  <label :for="'worktime_break_end_' + index + '_' + breakIndex">
                    bis
                  </label>
                </template>
                <template #input>
                  <input class="tw-mr-4" :id="'worktime_break_end_' + index + '_' + breakIndex"
                         :name="'worktime[' + index + '][breaks][' + breakIndex + '][end]'"
                         type="time" :disabled="!value.start || !value.end"
                         placeholder="Ende" v-model="breakEntry.end"
                         :class="{'tw-border-red-500': getFieldError(index, 'end', breakIndex)}"
                         @blur="markDirty(index, 'end', breakIndex)"
                         @change="debouncedValidate()">
                </template>
              </bordered-input>
              <p v-if="getFieldError(index, 'end', breakIndex)" class="tw-text-red-600 tw-text-sm tw-mt-1">
                {{ getFieldError(index, 'end', breakIndex) }}
              </p>
            </div>
            <button class="cu-btn-default" type="button" @click="removeBreak(index, breakIndex)">
              <i class="fa fa-trash-o"></i>
            </button>
          </div>

          <div class="tw-mt-4">
            <AddElementCard
                text="Pause hinzufügen"
                :fullWidth="true"
                :fullHeight="true"
                @click="addBreak(index)"
            />
          </div>
        </div>

        <div class="tw-flex tw-flex-col tw-h-full">
          <p class="tw-font-bold tw-mb-2">Gesamt</p>
          <div class="tw-grid tw-grid-cols-2 tw-gap-4">
            <div>
              <bordered-input>
                <template #label>
                  <label :for="'worktime_total_break_' + index">
                    Pause
                  </label>
                </template>
                <template #input>
                  <input class="tw-mr-4" :id="'worktime_total_break_' + index" :name="'worktime_total_break_' + index"
                         type="time"
                         placeholder="Pause" :value="entryBreakFormatted(value)" disabled>
                </template>
              </bordered-input>
            </div>
            <div>
              <bordered-input>
                <template #label>
                  <label :for="'worktime_total_' + index">
                    Arbeitszeit
                  </label>
                </template>
                <template #input>
                  <input class="tw-mr-4" :id="'worktime_total_' + index" :name="'worktime_total_' + index" type="time"
                         placeholder="Arbeitszeit" :value="entryWorktimeFormatted(value)" disabled>
                </template>
              </bordered-input>
            </div>
          </div>
          <div class="tw-mt-2 lg:tw-mt-auto tw-self-end">
            <button class="cu-btn-default" type="button" @click="removeRow(index)">
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </div>
      </div>

      <hr class="tw-my-4"/>
    </div>

    <div class="tw-mx-4 tw-h-16">
      <AddElementCard
          text="Arbeitszeit hinzufügen"
          :fullWidth="true"
          :fullHeight="true"
          @click="addRow()"
      />
    </div>

    <div v-if="this.totalWorktime > 60*10" class="tw-my-4 tw-text-red-600">
      <span class="tw-font-bold">Hinweis:</span> Eingabe überschreitet 10 Stunden. Bitte prüfen und gesetzliche Vorgaben
      beachten.
    </div>

    <div class="tw-my-4 tw-text-red-600" v-if="this.hasFaultyBreakTimes">
      <span class="tw-font-bold">Achtung!</span> Eintrag kann nicht gespeichert werden, da die gesetzliche Pausenzeit
      nicht eingehalten wurde.<br>
      Ab 6h Arbeitszeit: 30 Minuten Pause<br>
      Ab 9h Arbeitszeit: 45 Minuten Pause
    </div>

    <input :value="isEditState ? '1' : '0'" name="is_edit" type="hidden">
    <input :value="changeComment" name="change_comment" type="hidden">
    <input :value="afterSaveDate" name="after_save_date" type="hidden">

    <div class="tw-mb-16 tw-mt-4">
      <button type="button" @click="handleSubmit()"
              :disabled="this.hasFaultyBreakTimes || this.rows.length <= 0"
              :class="(this.hasFaultyBreakTimes || this.rows.length <= 0 ? 'tw-bg-charcoal-grey tw-cursor-not-allowed' : 'tw-bg-brand-primary') + ' tw-border-brand-primary tw-py-2 tw-px-8 tw-rounded tw-text-white'">Speichern</button>
    </div>

    <div v-if="showChangeModal" class="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center">
      <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-50" @click="showChangeModal = false"></div>
      <div class="tw-relative tw-bg-white tw-rounded tw-shadow-lg tw-p-6 tw-w-full tw-max-w-lg">
        <div class="tw-flex tw-justify-between tw-items-start">
          <h2 class="tw-text-xl tw-font-bold">Grund für die Änderung</h2>
          <i class="fa fa-times tw-text-xl tw-cursor-pointer" @click="showChangeModal = false"></i>
        </div>

        <hr class="tw-my-4">

        <p class="tw-mb-3 tw-text-sm tw-text-gray-600">Bitte gib an, warum du diese Arbeitszeit nachträglich
          änderst.</p>

        <textarea
            v-model="changeComment"
            :class="{'tw-border-red-500': changeCommentError, 'tw-border-border-input': !changeCommentError}"
            class="tw-w-full tw-border tw-rounded tw-p-2 tw-text-sm tw-outline-none focus:tw-border-brand-primary"
            placeholder="Grund für die Änderung..."
            rows="4"
            @input="changeCommentError = false"
        ></textarea>
        <p v-if="changeCommentError" class="tw-text-red-600 tw-text-sm tw-mt-1">Bitte gib einen Grund an.</p>

        <div class="tw-flex tw-justify-end tw-gap-3 tw-mt-4">
          <button class="cu-btn-default" type="button" @click="showChangeModal = false">Abbrechen</button>
          <button class="cu-btn" type="button" @click="confirmChange()">Speichern</button>
        </div>
      </div>
    </div>

    <div v-if="showUnsavedModal" class="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center">
      <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-50" @click="showUnsavedModal = false"></div>
      <div class="tw-relative tw-bg-white tw-rounded tw-shadow-lg tw-p-6 tw-w-full tw-max-w-lg">
        <div class="tw-flex tw-justify-between tw-items-start">
          <h2 class="tw-text-xl tw-font-bold">Ungespeicherte Änderungen</h2>
          <i class="fa fa-times tw-text-xl tw-cursor-pointer" @click="showUnsavedModal = false"></i>
        </div>

        <hr class="tw-my-4">

        <p class="tw-mb-3 tw-text-sm tw-text-gray-600">Du hast für diesen Tag noch nicht gespeicherte
          Eingaben. Möchtest du sie speichern, bevor du das Datum wechselst, oder verwerfen?</p>

        <div class="tw-flex tw-justify-end tw-gap-3 tw-mt-4">
          <button class="cu-btn-default" type="button" @click="showUnsavedModal = false">Abbrechen</button>
          <button class="cu-btn-default" type="button" @click="discardAndSwitch()">Verwerfen</button>
          <button class="cu-btn" type="button" @click="saveAndSwitch()">Speichern</button>
        </div>
      </div>
    </div>
  </div>
</template>
